// Org resolution — Role → Seat → Person on shift (§ 9.2). Pure.

import type { PlantRoleId } from '@/versions/v1/schema/plant-role';
import type { Crew, Person, Seat, SeatAssignment, SeatId } from '@/versions/v1/schema/seat';
import {
  BUNKER_HILL_ASSIGNMENTS,
  BUNKER_HILL_CREWS,
  BUNKER_HILL_PERSONS,
  BUNKER_HILL_SEATS,
  PRIMARY_SEAT_BY_ROLE,
  ROLE_SHORT_LABEL,
} from '@/versions/v1/shared/bunker-hill-org-seed';

export type OrgSeed = {
  seats: readonly Seat[];
  persons: readonly Person[];
  assignments: readonly SeatAssignment[];
  crews: readonly Crew[];
};

export const BUNKER_HILL_ORG: OrgSeed = {
  seats: BUNKER_HILL_SEATS,
  persons: BUNKER_HILL_PERSONS,
  assignments: BUNKER_HILL_ASSIGNMENTS,
  crews: BUNKER_HILL_CREWS,
};

export type ResolvedHandler = {
  role: PlantRoleId;
  seatId: SeatId;
  seatLabel: string;
  name: string;
  since?: string;
};

function seatForRole(roleId: PlantRoleId, org: OrgSeed): Seat | undefined {
  const seatId = PRIMARY_SEAT_BY_ROLE[roleId];
  if (seatId) return org.seats.find((seat) => seat.id === seatId);
  return org.seats.find((seat) => seat.roleId === roleId);
}

function personOnSeat(seatId: SeatId, at: string, org: OrgSeed): Person | undefined {
  const assignment = org.assignments.find((row) => {
    if (row.seatId !== seatId) return false;
    if (row.from > at) return false;
    if (row.to && row.to < at) return false;
    return true;
  });
  if (!assignment) return undefined;
  return org.persons.find((person) => person.id === assignment.personId);
}

/** Role → seat → person on shift at `at`. Falls back to seat label when roster
 * has no assignment (honest for unseeded seats). */
export function resolveHandler(
  roleId: PlantRoleId,
  at: string,
  org: OrgSeed = BUNKER_HILL_ORG,
): ResolvedHandler | null {
  const seat = seatForRole(roleId, org);
  if (!seat) return null;
  const person = personOnSeat(seat.id, at, org);
  return {
    role: roleId,
    seatId: seat.id,
    seatLabel: seat.label,
    name: person?.name ?? seat.label,
  };
}

export function seatLabelById(seatId: SeatId | string, org: OrgSeed = BUNKER_HILL_ORG): string {
  return org.seats.find((row) => row.id === seatId)?.label ?? seatId;
}

export function roleShortLabel(roleId: PlantRoleId): string {
  return ROLE_SHORT_LABEL[roleId] ?? roleId;
}
